import { AbsoluteMinimumsProps } from '@/interfaces/absolute-minimums'

export const ALL_OTHER_STATES_DEFAULT = 194

export const CORE_STATES = [
  'Colorado',
  'Idaho',
  'Montana',
  'Nebraska',
  'South Dakota',
  'Utah',
  'Washington',
  'Wyoming',
]

export const ENABLED_STATES = [
  'California',
  'Nevada',
  'Oregon',
  'Arizona',
  'Connecticut',
  'District of Columbia',
  'Delaware',
  'Illinois',
  'Indiana',
  'Kentucky',
  'Maryland',
  'Michigan',
  'North Carolina',
  'New Jersey',
  'New York',
  'Ohio',
  'Pennsylvania',
  'South Carolina',
  'Virginia',
  'Wisconsin',
  'West Virginia',
]

// Keeps the first CORE_STATES.length entries in place, sorts any
// additional destination states alphabetically by full state name, and
// always keeps the 'AOS' (All Other States) entry last.
export const sortDestinationEntries = <T extends { state: string }>(
  entries: T[]
): T[] => {
  const core = entries.slice(0, CORE_STATES.length)
  const rest = entries.slice(CORE_STATES.length)

  const aosIndex = rest.findIndex((entry) => entry.state === 'AOS')
  const aos = aosIndex !== -1 ? rest.splice(aosIndex, 1)[0] : undefined

  rest.sort((a, b) => {
    const nameA =
      ABBREVIATION_TO_STATE[a.state as keyof typeof ABBREVIATION_TO_STATE] ||
      a.state
    const nameB =
      ABBREVIATION_TO_STATE[b.state as keyof typeof ABBREVIATION_TO_STATE] ||
      b.state
    return nameA.localeCompare(nameB)
  })

  return aos ? [...core, ...rest, aos] : [...core, ...rest]
}

export const ALL_STATES = [
  'Alabama',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
]

export const STATE_TO_ABBREVIATIONS = {
  Alabama: 'AL',
  Arizona: 'AZ',
  Arkansas: 'AR',
  California: 'CA',
  Colorado: 'CO',
  Connecticut: 'CT',
  Delaware: 'DE',
  'District of Columbia': 'DC',
  Florida: 'FL',
  Georgia: 'GA',
  Idaho: 'ID',
  Illinois: 'IL',
  Indiana: 'IN',
  Iowa: 'IA',
  Kansas: 'KS',
  Kentucky: 'KY',
  Maine: 'ME',
  Maryland: 'MD',
  Massachusetts: 'MA',
  Michigan: 'MI',
  Minnesota: 'MN',
  Mississippi: 'MS',
  Missouri: 'MO',
  Montana: 'MT',
  Nebraska: 'NE',
  Nevada: 'NV',
  'New Hampshire': 'NH',
  'New Jersey': 'NJ',
  'New Mexico': 'NM',
  'New York': 'NY',
  'North Carolina': 'NC',
  'North Dakota': 'ND',
  Ohio: 'OH',
  Oklahoma: 'OK',
  Oregon: 'OR',
  Pennsylvania: 'PA',
  'Rhode Island': 'RI',
  'South Carolina': 'SC',
  'South Dakota': 'SD',
  Tennessee: 'TN',
  Texas: 'TX',
  Utah: 'UT',
  Vermont: 'VT',
  Virginia: 'VA',
  Washington: 'WA',
  'West Virginia': 'WV',
  Wisconsin: 'WI',
  Wyoming: 'WY',
}

export const ABBREVIATION_TO_STATE = {
  AL: 'Alabama',
  AZ: 'Arizona',
  AR: 'Arkansas',
  CA: 'California',
  CO: 'Colorado',
  CT: 'Connecticut',
  DE: 'Delaware',
  DC: 'District of Columbia',
  FL: 'Florida',
  GA: 'Georgia',
  ID: 'Idaho',
  IL: 'Illinois',
  IN: 'Indiana',
  IA: 'Iowa',
  KS: 'Kansas',
  KY: 'Kentucky',
  ME: 'Maine',
  MD: 'Maryland',
  MA: 'Massachusetts',
  MI: 'Michigan',
  MN: 'Minnesota',
  MS: 'Mississippi',
  MO: 'Missouri',
  MT: 'Montana',
  NE: 'Nebraska',
  NV: 'Nevada',
  NH: 'New Hampshire',
  NJ: 'New Jersey',
  NM: 'New Mexico',
  NY: 'New York',
  NC: 'North Carolina',
  ND: 'North Dakota',
  OH: 'Ohio',
  OK: 'Oklahoma',
  OR: 'Oregon',
  PA: 'Pennsylvania',
  RI: 'Rhode Island',
  SC: 'South Carolina',
  SD: 'South Dakota',
  TN: 'Tennessee',
  TX: 'Texas',
  UT: 'Utah',
  VT: 'Vermont',
  VA: 'Virginia',
  WA: 'Washington',
  WV: 'West Virginia',
  WI: 'Wisconsin',
  WY: 'Wyoming',
}

export enum StateAbbreviationEnum {
  AL = 'AL',
  AZ = 'AZ',
  AR = 'AR',
  CA = 'CA',
  CO = 'CO',
  CT = 'CT',
  DE = 'DE',
  DC = 'DC',
  FL = 'FL',
  GA = 'GA',
  ID = 'ID',
  IL = 'IL',
  IN = 'IN',
  IA = 'IA',
  KS = 'KS',
  KY = 'KY',
  ME = 'ME',
  MD = 'MD',
  MA = 'MA',
  MI = 'MI',
  MN = 'MN',
  MS = 'MS',
  MO = 'MO',
  MT = 'MT',
  NE = 'NE',
  NV = 'NV',
  NH = 'NH',
  NJ = 'NJ',
  NM = 'NM',
  NY = 'NY',
  NC = 'NC',
  ND = 'ND',
  OH = 'OH',
  OK = 'OK',
  OR = 'OR',
  PA = 'PA',
  RI = 'RI',
  SC = 'SC',
  SD = 'SD',
  TN = 'TN',
  TX = 'TX',
  UT = 'UT',
  VT = 'VT',
  VA = 'VA',
  WA = 'WA',
  WV = 'WV',
  WI = 'WI',
  WY = 'WY',
}

export enum StateNameEnum {
  Alabama = 'Alabama',
  Arizona = 'Arizona',
  Arkansas = 'Arkansas',
  California = 'California',
  Colorado = 'Colorado',
  Connecticut = 'Connecticut',
  Delaware = 'Delaware',
  DistrictOfColumbia = 'District of Columbia',
  Florida = 'Florida',
  Georgia = 'Georgia',
  Idaho = 'Idaho',
  Illinois = 'Illinois',
  Indiana = 'Indiana',
  Iowa = 'Iowa',
  Kansas = 'Kansas',
  Kentucky = 'Kentucky',
  Maine = 'Maine',
  Maryland = 'Maryland',
  Massachusetts = 'Massachusetts',
  Michigan = 'Michigan',
  Minnesota = 'Minnesota',
  Mississippi = 'Mississippi',
  Missouri = 'Missouri',
  Montana = 'Montana',
  Nebraska = 'Nebraska',
  Nevada = 'Nevada',
  NewHampshire = 'New Hampshire',
  NewJersey = 'New Jersey',
  NewMexico = 'New Mexico',
  NewYork = 'New York',
  NorthCarolina = 'North Carolina',
  NorthDakota = 'North Dakota',
  Ohio = 'Ohio',
  Oklahoma = 'Oklahoma',
  Oregon = 'Oregon',
  Pennsylvania = 'Pennsylvania',
  RhodeIsland = 'Rhode Island',
  SouthCarolina = 'South Carolina',
  SouthDakota = 'South Dakota',
  Tennessee = 'Tennessee',
  Texas = 'Texas',
  Utah = 'Utah',
  Vermont = 'Vermont',
  Virginia = 'Virginia',
  Washington = 'Washington',
  WestVirginia = 'West Virginia',
  Wisconsin = 'Wisconsin',
  Wyoming = 'Wyoming',
}

export const downloadMinimumsCSV = (
  minimums: AbsoluteMinimumsProps[],
  accountNumber: string | undefined
) => {
  const today = new Date()

  const yyyy = today.getFullYear()
  const mm = String(today.getMonth() + 1).padStart(2, '0')
  const dd = String(today.getDate()).padStart(2, '0')

  const dateStr = `${mm}-${dd}-${yyyy}`

  const fileName = `${accountNumber} Absolute Minimums ${dateStr}`
  const rows: string[] = []

  rows.push('origin,name,type,destination,rate')

  minimums.forEach((originObj) => {
    const { origin, name } = originObj

    ;(['direct', 'partnerDirect', 'nonDirect'] as const).forEach((type) => {
      originObj[type].forEach((entry) => {
        rows.push(`${origin},${name},${type},${entry.state},${entry.rate}`)
      })
    })
  })

  const csvContent = rows.join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)

  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', fileName)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export const uploadMinimumsCSV = async (
  file: File,
  setMinimums: (data: any[]) => void,
  setError: (msg: string | null) => void
) => {
  const { minimums, error } = await validateFile(file)

  if (minimums) {
    setError(null)
    setMinimums(minimums)
    return
  }

  setError(error)
}

const REQUIRED_ORIGINS = CORE_STATES.map(
  (s) => STATE_TO_ABBREVIATIONS[s as keyof typeof STATE_TO_ABBREVIATIONS]
)
const REQUIRED_DESTS = [...REQUIRED_ORIGINS]
const OPTIONAL_DESTS = ENABLED_STATES.map(
  (s) => STATE_TO_ABBREVIATIONS[s as keyof typeof STATE_TO_ABBREVIATIONS]
)
const VALID_DESTS = [...REQUIRED_DESTS, ...OPTIONAL_DESTS, 'AOS']
const TYPES = ['direct', 'partnerDirect', 'nonDirect']

const validateFile = (
  file: File
): Promise<{
  minimums: any[] | null
  error: string | null
}> => {
  return new Promise((resolve) => {
    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      resolve({
        minimums: null,
        error: 'Invalid file type. Please upload a CSV file.',
      })
      return
    }

    const reader = new FileReader()

    reader.onload = (event) => {
      try {
        const text = event.target?.result as string

        const rows = text
          .split('\n')
          .map((r) => r.trim())
          .filter(Boolean)

        const header = rows[0].toLowerCase()
        // Validate header (origin,name,type,destination,rate)
        if (header !== 'origin,name,type,destination,rate') {
          resolve({
            minimums: null,
            error:
              'Invalid CSV header. Expected: origin,name,type,destination,rate',
          })
          return
        }

        const [, ...lines] = rows
        const resultMap: Record<string, any> = {}
        const structure: Record<string, Record<string, Set<string>>> = {}

        for (let index = 0; index < lines.length; index++) {
          const line = lines[index]
          const parts = line.split(',')
          // Validate each row has 5 values, 1 for each header
          if (parts.length !== 5) {
            resolve({
              minimums: null,
              error: `Invalid format on row ${index + 2}.`,
            })
            return
          }

          const [origin, name, type, destination, rate] = parts.map((p) =>
            p.trim()
          )

          // Validate Origin states
          if (!REQUIRED_ORIGINS.includes(origin)) {
            resolve({
              minimums: null,
              error: `Invalid origin "${origin}" on row ${
                index + 2
              }. Must be one of: ${REQUIRED_ORIGINS.join(', ')}.`,
            })
            return
          }

          const expectedName =
            ABBREVIATION_TO_STATE[origin as keyof typeof ABBREVIATION_TO_STATE]

          // Validate origin name
          if (name !== expectedName) {
            resolve({
              minimums: null,
              error: `Invalid name "${name}" for origin "${origin}" on row ${
                index + 2
              }. Expected "${expectedName}".`,
            })
            return
          }

          // Validate destination states (origin states + partner direct states)
          if (!VALID_DESTS.includes(destination)) {
            resolve({
              minimums: null,
              error: `Invalid destination "${destination}" on row ${
                index + 2
              }. Must be one of: ${VALID_DESTS.join(', ')}.`,
            })
            return
          }

          // Validate the service type
          if (!TYPES.includes(type)) {
            resolve({
              minimums: null,
              error: `Invalid type "${type}" on row ${
                index + 2
              }. Must be direct, nonDirect, or partnerDirect.`,
            })
            return
          }

          // Validate non-negative number
          if (!/^\d+(\.\d+)?$/.test(rate)) {
            resolve({
              minimums: null,
              error: `Invalid rate "${rate}" on row ${
                index + 2
              }. Must be a non-negative number.`,
            })
            return
          }

          // Round to the hundredth place
          const roundedRate = Math.round(parseFloat(rate) * 100) / 100

          // Build resultMap for output
          if (!resultMap[origin]) {
            resultMap[origin] = {
              origin,
              name,
              direct: [],
              nonDirect: [],
              partnerDirect: [],
            }
          }
          resultMap[origin][type].push({
            state: destination,
            rate: roundedRate,
          })

          // Build structure for format validation
          if (!structure[origin]) structure[origin] = {}
          if (!structure[origin][destination])
            structure[origin][destination] = new Set()
          structure[origin][destination].add(type)
        }

        // Format validation phase

        const origins = Object.keys(structure)
        // Validate Origins
        if (origins.length !== REQUIRED_ORIGINS.length) {
          resolve({
            minimums: null,
            error: `CSV must contain exactly 8 origins: ${REQUIRED_ORIGINS.join(
              ', '
            )}. Found: ${origins.join(', ')}`,
          })
          return
        }

        // Reference destination set from first origin
        const refDests = Object.keys(structure[origins[0]])
        for (const origin of origins) {
          const dests = Object.keys(structure[origin])

          // Must include all required destinations
          for (const req of REQUIRED_DESTS) {
            if (!dests.includes(req)) {
              resolve({
                minimums: null,
                error: `Origin "${origin}" is missing required destination "${req}".`,
              })
              return
            }
          }

          // Validate destination length
          if (
            dests.length < REQUIRED_DESTS.length ||
            dests.length > VALID_DESTS.length
          ) {
            resolve({
              minimums: null,
              error: `Origin "${origin}" must have between ${REQUIRED_DESTS.length} and ${VALID_DESTS.length} destinations. Found ${dests.length}.`,
            })
            return
          }

          // All Origins must have the same destinations
          if (dests.sort().join(',') !== refDests.sort().join(',')) {
            resolve({
              minimums: null,
              error: `Destination set mismatch for origin "${origin}". All origins must have the same destinations.`,
            })
            return
          }

          // Each (origin,dest) must have all 3 types
          for (const dest of dests) {
            const typeSet = structure[origin][dest]
            for (const t of TYPES) {
              if (!typeSet.has(t)) {
                resolve({
                  minimums: null,
                  error: `Missing type "${t}" for origin "${origin}" to destination "${dest}".`,
                })
                return
              }
            }
          }
        }

        const result = Object.values(resultMap)
        resolve({ minimums: result, error: null })
      } catch {
        resolve({
          minimums: null,
          error: 'An unexpected error occurred while processing the CSV.',
        })
      }
    }

    reader.onerror = () => {
      resolve({
        minimums: null,
        error: 'Failed to read the file. Please try again.',
      })
    }

    reader.readAsText(file)
  })
}
